import {
  effectivePlanPriceCents,
  formatPlanPrice,
  isCustomQuotePlan,
  sortPlans,
  type PublicPlan,
} from '@/content/plans';
import { appTiers, sortApps, tierOf, usageUnitFor, type PublicApp } from '@/content/apps';

export const calculatorPath = '/calculadora';

export interface PlanSuggestion {
  plan: PublicPlan;
  /** Assentos além do incluso (necessidade − max_users do plano). */
  overflowSeats: number;
  /** Canais além do incluso. */
  overflowChannels: number;
  /** Nenhum plano público cobre a necessidade sem overflow/Enterprise. */
  needsEnterprise: boolean;
}

/** Planos compráveis online (sem cotação custom). */
export function buyablePlans(plans: PublicPlan[]): PublicPlan[] {
  return sortPlans(plans).filter((p) => !isCustomQuotePlan(p) && effectivePlanPriceCents(p) > 0);
}

/**
 * Sugere o plano mais barato que cabe em assentos + canais.
 * Se nenhum couber, usa o maior plano público e calcula overflow de extras.
 */
export function suggestPlan(
  plans: PublicPlan[],
  seats: number,
  channels: number,
): PlanSuggestion | null {
  const catalog = buyablePlans(plans);
  if (catalog.length === 0) return null;

  const needSeats = Math.max(1, Math.floor(seats));
  const needChannels = Math.max(1, Math.floor(channels));

  const fitting = catalog.filter(
    (p) =>
      (p.max_users == null || p.max_users >= needSeats) &&
      (p.max_connections == null || p.max_connections >= needChannels),
  );

  if (fitting.length > 0) {
    const plan = [...fitting].sort(
      (a, b) => effectivePlanPriceCents(a) - effectivePlanPriceCents(b),
    )[0];
    return { plan, overflowSeats: 0, overflowChannels: 0, needsEnterprise: false };
  }

  const largest = [...catalog].sort((a, b) => {
    const users = (b.max_users ?? 0) - (a.max_users ?? 0);
    if (users !== 0) return users;
    return (b.max_connections ?? 0) - (a.max_connections ?? 0);
  })[0];

  const overflowSeats = Math.max(0, needSeats - (largest.max_users ?? 0));
  const overflowChannels = Math.max(0, needChannels - (largest.max_connections ?? 0));
  const stillShort =
    largest.max_users == null ||
    largest.max_connections == null ||
    needSeats > (largest.max_users ?? 0) + 50 ||
    needChannels > (largest.max_connections ?? 0) + 20;

  return {
    plan: largest,
    overflowSeats,
    overflowChannels,
    needsEnterprise: stillShort && overflowSeats + overflowChannels > 0,
  };
}

/** Add-ons selecionáveis na calculadora. */
export function purchasableAddons(apps: PublicApp[]): PublicApp[] {
  return sortApps(apps).filter(
    (a) =>
      a.tier === 'addon' &&
      a.availability === 'available' &&
      (a.pricing?.monthly_price_cents ?? 0) > 0,
  );
}

/** Preço aproximado (~) quando há excedente ou consumo sem teto claro (ex. IA). */
export function isApproximateAddonPrice(app: PublicApp, tierCode?: string | null): boolean {
  const tier = tierOf(app, tierCode);
  const p = tier
    ? { ...app.pricing, overage_unit_price_cents: tier.overage_unit_price_cents, usage_unit: app.pricing?.usage_unit ?? null }
    : app.pricing;
  if (!p) return false;
  if (p.overage_unit_price_cents != null && p.overage_unit_price_cents > 0) return true;
  // IA e similares: usage_unit sem overage publicável = consumo variável
  if (p.usage_unit && (p.overage_unit_price_cents == null || p.overage_unit_price_cents === 0)) {
    return true;
  }
  return false;
}

/** Preço mensal do add-on: o do nível escolhido, ou o do mais barato quando nenhum foi escolhido. */
export function addonPriceCents(app: PublicApp, tierCode?: string | null): number {
  return tierOf(app, tierCode)?.monthly_price_cents ?? app.pricing?.monthly_price_cents ?? 0;
}

export function formatAddonCalculatorPrice(app: PublicApp, tierCode?: string | null): string {
  const base = formatPlanPrice(addonPriceCents(app, tierCode));
  return isApproximateAddonPrice(app, tierCode) ? `~ ${base}` : base;
}

export function addonUnlockHint(app: PublicApp, tierCode?: string | null): string | null {
  const tier = tierOf(app, tierCode);
  const included = tier ? tier.included_usage : (app.pricing?.included_usage ?? null);
  const unit = app.pricing?.usage_unit;
  if (!included || included <= 0 || !unit) return null;
  return `Libera ${included.toLocaleString('pt-BR')} ${usageUnitFor(unit, included)}/mês`;
}

/** Nível escolhido de cada add-on com níveis; o que não tem nível, ou veio inválido, cai no mais barato. */
export function resolveAddonTiers(
  apps: PublicApp[],
  addonSlugs: string[],
  chosen: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const slug of addonSlugs) {
    const app = apps.find((a) => a.slug === slug);
    if (!app || appTiers(app).length === 0) continue;
    const tier = tierOf(app, chosen[slug]);
    if (tier) out[slug] = tier.code;
  }
  return out;
}

/** `tiers=customer-success:completo,outro:basico` → `{ 'customer-success': 'completo', … }`. */
export function parseTiersParam(raw: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of (raw ?? '').split(',')) {
    const [slug, code] = part.split(':').map((x) => x.trim());
    if (slug && code) out[slug] = code;
  }
  return out;
}

export function planDescription(plan: PublicPlan): string {
  const seats = plan.max_users;
  const channels = plan.max_connections;
  const parts: string[] = [];
  if (seats != null) parts.push(`${seats} usuário${seats === 1 ? '' : 's'}`);
  if (channels != null) parts.push(`${channels} ${channels === 1 ? 'canal' : 'canais'}`);
  if (plan.active_quota_monthly > 0) {
    parts.push(`até ${plan.active_quota_monthly.toLocaleString('pt-BR')} msgs/mês`);
  }
  return parts.join(' · ') || 'Assinatura mensal';
}

export function extraSeatUnitCents(plan: PublicPlan): number {
  return plan.extra_seat_monthly_cents ?? 0;
}

export function extraChannelUnitCents(plan: PublicPlan): number {
  return plan.extra_connection_monthly_cents ?? 0;
}

export function computeCalculatorTotalCents(opts: {
  plan: PublicPlan | null;
  addonSlugs: string[];
  apps: PublicApp[];
  extraSeats: number;
  extraChannels: number;
  /** Nível escolhido por add-on (`slug -> código`). */
  addonTiers?: Record<string, string>;
}): number {
  const { plan, addonSlugs, apps, extraSeats, extraChannels, addonTiers = {} } = opts;
  if (!plan) return 0;
  let total = effectivePlanPriceCents(plan);
  const bySlug = new Map(apps.map((a) => [a.slug, a]));
  for (const slug of addonSlugs) {
    const app = bySlug.get(slug);
    if (app?.pricing) total += addonPriceCents(app, addonTiers[slug]);
  }
  total += Math.max(0, extraSeats) * extraSeatUnitCents(plan);
  total += Math.max(0, extraChannels) * extraChannelUnitCents(plan);
  return total;
}

export function calculatorCheckoutHref(opts: {
  planCode: string;
  addonSlugs: string[];
  extraSeats: number;
  extraChannels: number;
  addonTiers?: Record<string, string>;
}): string {
  const params = new URLSearchParams();
  params.set('plano', opts.planCode);
  if (opts.addonSlugs.length > 0) params.set('addons', opts.addonSlugs.join(','));
  const tiers = Object.entries(opts.addonTiers ?? {}).filter(([slug]) => opts.addonSlugs.includes(slug));
  if (tiers.length > 0) params.set('tiers', tiers.map(([slug, code]) => `${slug}:${code}`).join(','));
  if (opts.extraSeats > 0) params.set('extra_seats', String(opts.extraSeats));
  if (opts.extraChannels > 0) params.set('extra_channels', String(opts.extraChannels));
  return `/assinar?${params.toString()}`;
}
